import type { Annotation, AnnotationType } from "./types";
import { COLORS } from "./theme";

/** Marker colour + label per change type (categorical, sourced from the tokens). */
export const ANNOTATION_TYPE_META: Record<AnnotationType, { label: string; color: string }> = {
  budget: { label: "Budget", color: COLORS.series1 }, // blue
  status: { label: "Status", color: COLORS.series3 }, // pink
  creative: { label: "Creative", color: COLORS.series2 }, // green
  targeting: { label: "Targeting", color: COLORS.warning }, // amber
  audience: { label: "Audience", color: COLORS.violet }, // violet
  external: { label: "External", color: COLORS.slate }, // slate
  other: { label: "Other", color: COLORS.slate }, // slate
};

export const ANNOTATION_TYPES: AnnotationType[] = [
  "budget",
  "status",
  "creative",
  "targeting",
  "audience",
  "external",
  "other",
];

/** An auto marker with no human "why" yet — the "needs explaining" state. */
export function isUnannotatedAuto(a: Annotation): boolean {
  return a.source === "meta_auto" && !(a.note && a.note.trim());
}

/** Group annotations by their chart date. */
export function markersByDate(annotations: Annotation[]): Map<string, Annotation[]> {
  const m = new Map<string, Annotation[]>();
  for (const a of annotations) {
    const arr = m.get(a.date);
    if (arr) arr.push(a);
    else m.set(a.date, [a]);
  }
  return m;
}

/** One vertical marker per date, aggregated (this is the day-level clustering). */
export interface ChartMarker {
  date: string;
  color: string;
  /** false when ≥1 auto marker that day still lacks a human "why" (→ hollow). */
  annotated: boolean;
  count: number;
  /** Short hover text. */
  label: string;
}

export function toChartMarkers(annotations: Annotation[]): ChartMarker[] {
  const out: ChartMarker[] = [];
  for (const [date, list] of markersByDate(annotations)) {
    const types = [...new Set(list.map((a) => a.type))];
    const color = types.length === 1 ? ANNOTATION_TYPE_META[types[0]!].color : "#94A3B8";
    const annotated = !list.some(isUnannotatedAuto);
    const first = list[0]!;
    const label = list.length === 1 ? first.autoSummary ?? first.note ?? "Change" : `${list.length} changes`;
    out.push({ date, color, annotated, count: list.length, label });
  }
  return out;
}
